import React, { useEffect, useState } from 'react';
import templateService from '../../../services/template.service';
import { FieldDefinition } from '../../../contexts/FieldDefinitionsContext';
import CustomFilterDialogBase, { type CustomFilterDialogProps } from './CustomFilterDialogBase';

/*
 * Templates Custom Filter — the templates binding of the shared condition
 * builder (CustomFilterDialogBase).
 *
 * Fields come from the SERVER (GET /templates/filter-fields), derived from the
 * backend's TEMPLATE_FILTER_REGISTRY — the same table the SQL translator
 * compiles from — so the picker can never offer a field the server cannot
 * translate (an unknown field fails OPEN server-side, i.e. drift would silently
 * return every row). There is deliberately NO hand-written field list here.
 *
 * Saved filters live in quote_custom_filters with scope='templates', so a
 * template filter never collides with the same user's quote filter of the same
 * name.
 */

const TemplateCustomFilterDialog: React.FC<CustomFilterDialogProps> = (props) => {
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinition[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);

  // Fetch on every open, deliberately: the SERVICE owns the once-per-session
  // guarantee (its promise cache), so a reopen is a cache hit with no HTTP.
  // Guarding here with a ref instead would brick the dialog — this component is
  // never unmounted (the base holds the `return null`), so the cleanup below
  // runs on CLOSE; closing before the fetch settles would then discard the
  // result AND keep the ref set, stranding the picker on "Loading fields..."
  // forever. Re-running also buys a free retry after a transient failure.
  useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    setLoadingFields(true);
    templateService
      .getFilterFields()
      .then((defs) => { if (!cancelled) setFieldDefinitions(defs); })
      .catch(() => {
        // Console-silent by design: a transient failure (network / 5xx) must
        // leave the dialog usable rather than crash it. A missing endpoint grant
        // never lands here — the service turns a 403 into a resolved [].
        if (!cancelled) setFieldDefinitions([]);
      })
      .finally(() => { if (!cancelled) setLoadingFields(false); });
    return () => { cancelled = true; };
  }, [props.open]);

  return (
    <CustomFilterDialogBase
      {...props}
      fieldDefinitions={fieldDefinitions}
      loadingFields={loadingFields}
      listFilters={() => templateService.listCustomFilters()}
      saveFilter={(name, filter) => templateService.saveCustomFilter(name, filter)}
    />
  );
};

export default TemplateCustomFilterDialog;
